import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { safeConferenceReturn, closingPath, editClosingPath } from "@/lib/conference";
import { api, fileUrl, formatApiError } from "@/lib/api";
import {
  ArrowLeft, Check, X, RefreshCcw, FileText, Loader2, History as HistoryIcon, Fuel,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const statusColors = {
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
  conferido: "bg-green-100 text-green-800 border-green-200",
  rejeitado: "bg-red-100 text-red-800 border-red-200",
  correcao: "bg-orange-100 text-orange-800 border-orange-200",
};

export default function ReviewClosing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeConferenceReturn(params.get("returnTo")) || "/admin/fechamentos";
  const fromConference = returnTo !== "/admin/fechamentos";
  const [retry, setRetry] = useState(0);
  const [doc, setDoc] = useState(null);
  const [changes, setChanges] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [justificativa, setJustificativa] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true); setDoc(null); setChanges([]); setSiblings([]);
    window.scrollTo?.(0, 0);
    async function load() {
      try {
        const { data } = await api.get(`/closings/${id}`, { signal: controller.signal });
        if (!active) return;
        setDoc(data); setJustificativa(data?.review?.justificativa || "");
        const [history, routes] = await Promise.allSettled([
          api.get(`/closings/${id}/changes`, { signal: controller.signal }),
          api.get("/closings", { signal: controller.signal, params: { driver_id: data.driver_id, date_from: data.date, date_to: data.date } })
        ]);
        if (!active) return;
        setChanges(history.status === "fulfilled" ? history.value.data || [] : []);
        setSiblings(routes.status === "fulfilled" ? (routes.value.data || []).filter(row => row.id !== data.id) : []);
      } catch (err) { if (active && err.code !== "ERR_CANCELED") toast.error(formatApiError(err)); }
      finally { if (active) setLoading(false); }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [id, retry]);

  async function act(action) {
    if ((action === "rejeitar" || action === "corrigir") && !justificativa.trim()) {
      toast.error("Informe a justificativa");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/closings/${id}/review`, { action, justificativa, expected_updated_at: doc.updated_at || null });
      toast.success(`Fechamento ${action === "aprovar" ? "aprovado" : action === "rejeitar" ? "rejeitado" : "enviado para correção"}`);
      navigate(returnTo);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const back = <Link to={returnTo} className="review-return"><ArrowLeft size={18} />{fromConference ? "Voltar à conferência" : "Voltar aos fechamentos"}</Link>;
  if (loading) return <div>{back}<p className="mt-4 text-slate-500">Carregando fechamento…</p></div>;
  if (!doc) return <div>{back}<p className="mt-4">Não foi possível abrir o fechamento.</p><button className="op-secondary mt-3" onClick={() => setRetry(n => n + 1)}>Tentar novamente</button></div>;

  return (
    <div className="review-closing space-y-5">
      {back}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Conferência</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{doc.driver_nome}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {doc.veiculo} · {doc.placa} · Rota {doc.rota || "—"} · {doc.date}
          </p>
        </div>
        <Badge className={`border ${statusColors[doc.status] || ""}`}>{doc.status}</Badge>
      </div>

      {!doc.archived && <Link className="op-secondary inline-flex" to={editClosingPath(doc.id, doc.driver_id, doc.date, fromConference ? returnTo : null)}>Editar fechamento do motorista</Link>}

      {/* Numbers */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Stat label="Entregues" value={doc.entregues} accent="text-green-700" />
        <Stat label="Retornos" value={doc.retornos} accent="text-amber-700" />
        <Stat label="Fora Lista" value={doc.fora_lista_qtd} accent="text-red-700" />
        <Stat label="Total" value={doc.total} accent="text-slate-700" />
        <Stat label="Sucesso" value={`${doc.sucesso}%`} accent="text-blue-700" />
        <Stat label="Coletas" value={doc.coletas_qtd || 0} accent="text-cyan-700" />
      </div>
      {doc.modalidade_fechamento === "por_viagem" && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700">Fechamento por viagem — {doc.viagens_qtd || (doc.viagens || []).length} viagem(ns)</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(doc.viagens || []).map((viagem, index) => (
              <div key={viagem.id || index} className="rounded-lg border border-amber-200 bg-white p-3">
                <p className="font-semibold text-slate-900">Viagem {index + 1} · {viagem.rota_setor}</p>
                <p className="mb-3 text-sm text-slate-500">{viagem.remessas_qtd} remessas</p>
                <ImagePanel title="Comprovante" image={viagem.comprovante} />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">
            Devoluções — {doc.devolucao_qtd || 0} remessas ({(doc.devolucao_comprovantes || (doc.devolucao_comprovante ? [doc.devolucao_comprovante] : [])).length} fotos)
          </p>
          <div className="grid grid-cols-2 gap-3">
            {(doc.devolucao_comprovantes || (doc.devolucao_comprovante ? [doc.devolucao_comprovante] : [])).map((image, index) => (
              <ImagePanel key={image.id || index} title={`Devolução ${index + 1}`} image={image} />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">
            OnLog — {doc.onlog_qtd || (doc.onlog_comprovante ? 1 : 0)} remessas ({(doc.onlog_comprovantes || (doc.onlog_comprovante ? [doc.onlog_comprovante] : [])).length} fotos)
          </p>
          <div className="grid grid-cols-2 gap-3">
            {(doc.onlog_comprovantes || (doc.onlog_comprovante ? [doc.onlog_comprovante] : [])).map((image, index) => (
              <ImagePanel key={image.id || index} title={`OnLog ${index + 1}`} image={image} />
            ))}
          </div>
        </div>
      </div>
      {Number(doc.remessas_ar_qtd || 0) > 0 && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm font-semibold text-blue-800">
          Remessas AR realizadas: {doc.remessas_ar_qtd}
        </div>
      )}

      {/* Images */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ImagePanel
          title="Print Jadlog"
          image={doc.jadlog_image}
          testId="review-jadlog-image"
        />
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-blue-700">
            <FileText className="h-4 w-4" />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em]">Fora de Lista — comprovantes ({(doc.fora_lista_images || []).length})</p>
          </div>
          {(!doc.fora_lista_images || doc.fora_lista_images.length === 0) ? (
            <p className="text-sm text-slate-400">Nenhum comprovante.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="review-fora-grid">
              {doc.fora_lista_images.map((img) => (
                <a key={img.id} href={fileUrl(img.storage_path)} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg border border-slate-200">
                  <img src={fileUrl(img.storage_path)} alt="" className="aspect-square w-full object-cover transition group-hover:scale-105" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Abastecimento */}
      {doc.abastecimento_used && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5" data-testid="review-fuel-panel">
          <div className="mb-3 flex items-center gap-2 text-amber-800">
            <Fuel className="h-4 w-4" />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em]">Abastecimento</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Valor</p>
              <p className="font-display text-2xl font-bold text-amber-800" data-testid="review-fuel-valor">
                R$ {Number(doc.abastecimento_valor || 0).toFixed(2).replace(".", ",")}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Data</p>
              <p className="font-medium text-slate-900">
                {doc.abastecimento_data ? new Date(doc.abastecimento_data + "T00:00:00").toLocaleDateString("pt-BR") : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Comprovante</p>
              {doc.abastecimento_comprovante ? (
                <a
                  href={fileUrl(doc.abastecimento_comprovante.storage_path)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline"
                  data-testid="review-fuel-comp-link"
                >
                  <FileText className="h-4 w-4" /> Abrir
                </a>
              ) : (
                <span className="text-sm text-slate-400">—</span>
              )}
            </div>
          </div>
          {doc.abastecimento_obs && (
            <p className="mt-3 rounded-md bg-white/60 p-2 text-sm text-slate-700">
              <span className="text-xs uppercase tracking-wider text-slate-500">Obs:</span> {doc.abastecimento_obs}
            </p>
          )}
          {doc.abastecimento_comprovante && doc.abastecimento_comprovante.content_type?.startsWith("image/") && (
            <a href={fileUrl(doc.abastecimento_comprovante.storage_path)} target="_blank" rel="noreferrer" className="mt-3 block overflow-hidden rounded-lg border border-amber-200">
              <img src={fileUrl(doc.abastecimento_comprovante.storage_path)} alt="comprovante" className="max-h-80 w-full object-contain" />
            </a>
          )}
        </div>
      )}

      {/* Outras rotas do mesmo dia */}
      {siblings.length > 0 && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-5" data-testid="sibling-routes-panel">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">
            Outras rotas neste dia ({siblings.length})
          </p>
          <p className="text-xs text-slate-600">Consolidado: <span className="font-semibold">
            E {doc.entregues + siblings.reduce((s, x) => s + (x.entregues || 0), 0)}
            {" · "}R {doc.retornos + siblings.reduce((s, x) => s + (x.retornos || 0), 0)}
            {" · "}F {doc.fora_lista_qtd + siblings.reduce((s, x) => s + (x.fora_lista_qtd || 0), 0)}
          </span> (somando esta rota)</p>
          <ul className="mt-3 space-y-1.5 text-sm">
            {siblings.map((s) => (
              <li key={s.id} className="flex items-center justify-between rounded-md border border-blue-100 bg-white px-3 py-2">
                <Link to={closingPath(s.id, returnTo)} className="font-medium text-slate-900 hover:text-blue-700 hover:underline">
                  {s.rota || "(sem rota)"}
                </Link>
                <span className="font-mono text-xs text-slate-600">
                  E {s.entregues} · R {s.retornos} · F {s.fora_lista_qtd} · {s.sucesso}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Observações */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Observações do motorista</p>
        <p className="mt-2 text-sm text-slate-700">{doc.observacoes || <span className="text-slate-400">— sem observações —</span>}</p>
      </div>

      {/* Histórico de alterações */}
      {changes.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="closing-changes-log">
          <div className="mb-3 flex items-center gap-2 text-blue-700">
            <HistoryIcon className="h-4 w-4" />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em]">Histórico de alterações ({changes.length})</p>
          </div>
          <ol className="space-y-3">
            {changes.map((c) => (
              <li key={c.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium text-slate-900">{c.edited_by_nome}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(c.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <ul className="mt-2 space-y-1">
                  {c.changes.map((ch, i) => (
                    <li key={i} className="font-mono text-xs text-slate-700">
                      <span className="font-semibold text-blue-700">{ch.field}</span>:{" "}
                      <span className="line-through text-slate-400">{String(ch.old ?? "—") || "—"}</span>{" → "}
                      <span className="font-bold text-green-700">{String(ch.new ?? "—") || "—"}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      )}

      {fromConference && <Link to={returnTo} className="review-return">← Voltar à conferência deste motorista</Link>}
      {/* Action panel */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="space-y-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Justificativa (obrigatória ao rejeitar/solicitar correção)</Label>
          <Textarea
            data-testid="review-justificativa-input"
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            rows={3}
            placeholder="Ex: foto borrada, soma inconsistente, etc."
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => act("aprovar")} disabled={busy} className="bg-green-700 hover:bg-green-600 text-white" data-testid="review-aprovar-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1.5 h-4 w-4" /> Aprovar</>}
          </Button>
          <Button onClick={() => act("corrigir")} disabled={busy} variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-50" data-testid="review-corrigir-btn">
            <RefreshCcw className="mr-1.5 h-4 w-4" /> Solicitar correção
          </Button>
          <Button onClick={() => act("rejeitar")} disabled={busy} variant="outline" className="border-red-400 text-red-700 hover:bg-red-50" data-testid="review-rejeitar-btn">
            <X className="mr-1.5 h-4 w-4" /> Rejeitar
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-bold ${accent}`}>{value ?? 0}</p>
    </div>
  );
}

function ImagePanel({ title, image, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2 text-blue-700">
        <FileText className="h-4 w-4" />
        <p className="text-[10px] font-bold uppercase tracking-[0.25em]">{title}</p>
      </div>
      {image ? (
        <a href={fileUrl(image.storage_path)} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg border border-slate-200">
          <img src={fileUrl(image.storage_path)} alt={title} className="max-h-[480px] w-full object-contain" data-testid={testId} />
        </a>
      ) : (
        <p className="text-sm text-slate-400">Sem imagem.</p>
      )}
    </div>
  );
}
